import { TourAnchor, tourSelector } from "training/anchors";

/**
 * CLE-3015 — the checks behind the gated `fill` steps (`ITourStep.done`): Next stays disabled until the
 * fields the step asks for are filled. Only steps where pressing Next through an empty form leaves the
 * trainee stuck later are gated: Create and Send (2.15b) moves on only when the estimate is created, and
 * while it shows nothing outside its button can be clicked, so a form left empty can be fixed only by
 * stepping Back one step at a time.
 *
 * Each check reads the real form inside the highlighted element (`target`), by the field ids the page
 * renders; a field that is not on the page is not asked for (the gate must never trap the trainee).
 * The render tests in `training/fillGates.test.tsx` run every check against the real form, empty and
 * filled.
 */

type Field = HTMLInputElement | HTMLTextAreaElement;

const field = (root: ParentNode, selector: string) =>
  root.querySelector<Field>(selector);

/** The field has a non-blank value. A field that is not on the page counts as filled. */
const filled = (root: ParentNode, selector: string) => {
  const input = field(root, selector);
  return !input || input.value.trim() !== "";
};

/** A field the page answered for the trainee: "No deductible" / "No maximum" render blank with a placeholder. */
const answered = (root: ParentNode, selector: string) => {
  const input = field(root, selector);
  return (
    !input || input.value.trim() !== "" || input.disabled || !!input.placeholder
  );
};

const FULL_DATE = /^\d{2}\/\d{2}\/\d{4}$/;

const fullDate = (root: ParentNode, selector: string) => {
  const input = field(root, selector);
  return !input || FULL_DATE.test(input.value.trim());
};

/** The form's own messages ("First name is required.", "Invalid phone number") render as `small.p-error`. */
const noVisibleError = (root: ParentNode) => !root.querySelector(".p-error");

/** 2.3: first name, last name, a whole date of birth and a ten-digit phone number, with no error showing. */
export const patientDetailsDone = (target: HTMLElement) =>
  filled(target, "#firstName") &&
  filled(target, "#lastName") &&
  fullDate(target, "#dateOfBirth") &&
  (field(target, "#phoneNumber")?.value.replace(/\D/g, "").length ?? 10) ===
    10 &&
  noVisibleError(target);

/**
 * 2.4: an Account / Visit ID, and a whole date of service — or "Not scheduled", which takes the date field
 * off the page — with no error showing.
 */
export const visitDone = (target: HTMLElement) =>
  filled(target, "#accountNumber") &&
  fullDate(target, "#dateOfService") &&
  noVisibleError(target);

/** 2.7: the member ID (Run Eligibility, the next step, needs it). */
export const memberIdDone = (target: HTMLElement) =>
  filled(target, "#memberId");

/**
 * 2.8 / 2.8f: Remaining Deductible, Remaining Out-of-pocket Max and Coinsurance Percent. A blank
 * out-of-pocket max is priced as $0, i.e. already met (usePrice.ts sends `outOfPocketMaxInDollar || 0`), so
 * the estimate comes out at $0 — the trap both texts warn about. "No deductible" and "No maximum" (the
 * page's own answers) count as filled.
 */
export const benefitsDone = (target: HTMLElement) =>
  answered(target, "#deductible") &&
  answered(target, "#outOfPocketMax") &&
  answered(target, "#coinsurancePercent");

/**
 * 2.10: at least one service is on the estimate. The highlight is the search field; the services table
 * (the next step's anchor) renders below it, outside the highlight, once a code is added.
 */
export const serviceAdded = (target: HTMLElement) =>
  !!target.ownerDocument.querySelector(tourSelector(TourAnchor.ServicesTable));

/**
 * 3.9b: the PDF note has text and, where the account's PDF settings add an "Estimated Hours" box, it has a
 * number — 0 counts (the dialog keeps Save disabled until it has one: NoteToPdfDialog).
 */
export const pdfNoteDone = (target: HTMLElement) => {
  const hours = target.querySelector<HTMLInputElement>("input");
  return (
    !!field(target, "textarea") &&
    filled(target, "textarea") &&
    (!hours || hours.value.trim() !== "")
  );
};

/** 6.3: the note has text (Add, the next step, stays disabled until it has). The anchor is the textarea. */
export const noteTyped = (target: HTMLElement) =>
  target.matches("textarea, input")
    ? (target as Field).value.trim() !== ""
    : filled(target, "textarea") && !!field(target, "textarea");
